// app/src/features/timeline/renderers/renderDateAxisContents.jsx
// Timeline render helpers moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1. Each
// takes the column's render context first; the original parameters follow.
import { ResolvedAxisTick } from "../../../components/timeline/ResolvedOutcomeMark.jsx";
import TimelineBackdrop from "../../../components/timeline/TimelineBackdrop.jsx";
import { C } from "../../../lib/colors.js";
import { isUnseen } from "../../../lib/forecast/arrivals.js";
import { AXIS_H } from "../../../lib/timeline/geometry.js";
import { daysSince, GAP_LABEL_FONT, latestGapLabel } from "../../../lib/timeline/latestMark.js";
import { isConcluded } from "../../../lib/tracking.js";

// The date bar's contents (backdrop, dates, reforecast hashes, the line marks), at the foot of
// the timeline.
export function renderDateAxisContents(ctx) {
  const {
    active,
    activeMarkIso,
    fadeEndX,
    fadeStartX,
    fcMarkX,
    forecastingPhase,
    genX,
    liveSentinelIso,
    ppd,
    resScreenX,
    resolvedMark,
    searchMoments,
    showGenLine,
    showTodayLine,
    theme,
    themeRaw,
    tickOpacity,
    timelineToday,
    todayX,
    unseenArrivals,
    viewLeft,
    viewMomentIso,
    viewSnap,
    visTicks,
    vw,
  } = ctx;
  const dateLabels = visTicks
    .filter((t) => t.label)
    .map((t) => ({ ...t, sx: (t.x / ppd - viewLeft) * ppd }));
  // The stretch from the latest forecast's mark to the Today line, the time that forecast still
  // stands for. Live view only: a past version moves the Today line to its own moment.
  const latestX = liveSentinelIso ? fcMarkX(liveSentinelIso) : null;
  const latestDays = daysSince(liveSentinelIso, timelineToday?.getTime());
  const gap =
    !viewSnap &&
    latestX != null &&
    latestDays != null &&
    latestDays >= 1 &&
    todayX - latestX >= 4 &&
    !isConcluded(active)
      ? { from: Math.max(latestX, -10), to: Math.min(todayX, vw + 10) }
      : null;
  const gapLabel =
    gap &&
    latestGapLabel({
      fromX: latestX,
      toX: todayX,
      days: latestDays,
      // A date label's width taken as 0.6em a character; the gap label's 6 px clearance covers the rest.
      dateLabels: dateLabels
        .filter((t) => tickOpacity(t.sx) > 0)
        .map((t) => ({ x: t.sx, width: t.label.length * (t.major ? 11 : 9) * 0.6 })),
    });
  return (
    <>
      <TimelineBackdrop
        todayX={todayX}
        vw={vw}
        fadeStartX={fadeStartX}
        fadeEndX={fadeEndX}
        theme={theme}
      />
      {gap && gap.to > gap.from && (
        <div
          data-latest-gap=""
          style={{
            position: "absolute",
            left: gap.from,
            width: gap.to - gap.from,
            top: 0,
            bottom: 0,
            background: `color-mix(in srgb, ${C.white} 6%, transparent)`,
            pointerEvents: "none",
          }}
        />
      )}
      {gapLabel && (
        <div
          data-latest-gap-label=""
          style={{
            position: "absolute",
            left: gapLabel.x,
            transform: "translateX(-50%)",
            top: 8,
            fontSize: GAP_LABEL_FONT,
            color: C.gray,
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          {gapLabel.text}
        </div>
      )}
      {dateLabels.map((t, i) => {
        const op = tickOpacity(t.sx);
        if (op <= 0) return null;
        const baseOp = t.major ? 1 : 0.55;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: t.sx,
              transform: "translateX(-50%)",
              top: 7,
              fontSize: t.major ? 11 : 9,
              color: C.white,
              fontWeight: t.major ? "bold" : "normal",
              opacity: baseOp * op,
              whiteSpace: "nowrap",
            }}
          >
            {t.label}
          </div>
        );
      })}
      {/* Ticks are visual only; hover and click live on the full-height gold guide band
       rendered at the container level below, so hovering anywhere in the mark's
       column drives the same line. */}
      {/* One hash per forecast moment (the interactive set, matching nearestMarkIso). The
      latest forecast's stands out from the past ones: it is the forecast on screen at rest. */}
      {searchMoments.map((iso, i) => {
        const sx = fcMarkX(iso);
        if (sx == null || sx < -10 || sx > vw + 10) return null;
        const lit = activeMarkIso === iso || viewMomentIso === iso;
        const unseen = isUnseen(unseenArrivals, iso);
        const hot = lit || unseen;
        const latest = iso === liveSentinelIso;
        const inset = hot ? 0 : AXIS_H * (latest ? 0.15 : 0.25);
        return (
          <div
            key={`fc-mark-axis-${i}`}
            data-fc-mark={iso}
            {...(latest ? { "data-fc-mark-latest": "" } : {})}
            className={unseen ? "fc-arrival-mark" : undefined}
            style={{
              position: "absolute",
              left: sx,
              top: inset,
              bottom: inset,
              width: hot ? 3 : latest ? 2 : 1,
              background: hot ? C.action : C.white,
              // currentColor drives the .fc-arrival-mark pulse glow.
              color: C.action,
              opacity: hot ? 1 : latest ? 0.85 : 0.3,
              transform: "translateX(-50%)",
              // Lit/arriving mark rides ABOVE its neighbors with a gold glow so
              // the one selected reads clearly even when dense (hourly) unlit
              // 1px hashes visually merge into a faint band beside it. An unseen
              // arrival sits highest so its ring is never occluded.
              zIndex: unseen ? 16 : lit ? 15 : latest ? 2 : 1,
              // Leave box-shadow UNSET for an unseen mark so the .fc-arrival-mark
              // class owns its glow in BOTH motion modes — the pulse animation
              // (normal) or the static ring (reduced-motion). Setting an inline
              // "none" here would override the reduced-motion CSS glow (inline
              // beats a non-!important rule). A lit-only mark keeps its steady glow.
              boxShadow: unseen ? undefined : lit ? `0 0 4px ${C.action}` : "none",
              pointerEvents: "none",
              transition:
                "top 0.3s ease-out, bottom 0.3s ease-out, width 0.3s ease-out, opacity 0.3s ease-out, box-shadow 0.3s ease-out",
            }}
          />
        );
      })}
      {active && showTodayLine && todayX > -10 && todayX < vw + 10 && (
        <div
          style={{
            position: "absolute",
            left: todayX,
            top: 0,
            width: 1,
            height: "100%",
            background: themeRaw.today,
            opacity: 0.45,
            pointerEvents: "none",
          }}
        />
      )}
      {showGenLine && genX > -10 && genX < vw + 10 && (
        <div
          style={{
            position: "absolute",
            left: genX,
            top: 0,
            width: 1,
            height: "100%",
            background: `${themeRaw.gray}55`,
            pointerEvents: "none",
            transform: "translateX(-50%)",
          }}
        />
      )}
      {resolvedMark && <ResolvedAxisTick x={resolvedMark.x} vw={vw} theme={theme} />}
      {!resolvedMark && resScreenX !== null && resScreenX > -10 && resScreenX < vw + 10 && (
        <div
          key={`fline-axis-${active?.id || ""}`}
          style={{
            position: "absolute",
            left: resScreenX,
            top: 0,
            width: 0,
            height: "100%",
            borderLeft: `1px dashed ${themeRaw.accent}`,
            opacity: 0.7,
            pointerEvents: "none",
            transformOrigin: "center",
            animation:
              forecastingPhase === "awaiting"
                ? "vertGrowFromCenter 0.5s ease-out 0.5s both"
                : "none",
          }}
        />
      )}
    </>
  );
}
